import React, { useContext, useEffect, useMemo } from 'react';
import { hot } from 'react-hot-loader/root';
import { Route, Switch } from 'react-router-dom';
import CustomHelmet from 'components/CustomHelmet';
import Routes from 'Routes';
import { useSelector } from 'react-redux';
import { setConfig } from 'react-hot-loader';
import { ThemeProvider } from '@material-ui/core/styles';
import { hotjar } from 'react-hotjar';

import { HOTJAR_ID, HOTJAR_SURVEY_VERSION } from 'appenv';
import { selectLocalizedEventName } from 'models/virtual_event';
import { selectLocale } from 'models/localization';
import NetworkCheckModal from 'components/modals/network_check';
import ContentPageFrame from 'components/ContentPageFrame';
import ConcurrentSessionTracker from 'components/ConcurrentSessionTracker';
import CustomPageContainer from 'pages/custom';
import CustomPageContainerV2 from 'pages/custom_v2';
import ProductPage from 'pages/product';
import ProductDetailContainer from 'pages/product_detail';
import BoothPreviewPage from 'pages/preview/BoothPreview';
import MainLobbyContainer from 'pages/main_lobby/MainLobbyContainer';
import BoothProfileRootContainer from 'pages/booths';
import HallPage from 'pages/hall/HallPage';
import WebinarPageContainer from 'pages/webinar/WebinarPageContainer';
import SearchResultPageContainer from 'pages/search/SearchResultPageContainer';
import LandingPage from 'pages/landing';
import PageNoFound from 'pages/PageNoFound';
import WebinarDetailContainer from 'pages/webinar_detail/WebinarDetailContainer';
import WebinarSpeakerDetailsContainer from 'pages/webinar_detail/speaker_detail/WebinarSpeakerDetailsContainer';
import ParticipantPageContainer from 'pages/participant/ParticipantPageContainer';
import ContactsPageContainer from 'pages/contacts/ContactsPageContainer';
import SpeakersPageContainer from 'pages/speakers/SpeakersPageContainer';
import ParticipantPageContainerHKTDC from 'pages/participant_hktdc/ParticipantPageContainer';
import NetworkingLounge from 'pages/networking_lounge';
import useDefaultTheme from 'theme';
import ProfileModal from 'components/ProfileModal';
import { UserSessionContext } from 'components/UserSession';
import FirstTimeProfilePictureModal from 'components/modals/firstTimeProfilePicture';
import useMyProfileTreatment from 'hooks/useMyProfileTreatment';
import useEnableHotjarTreatment from 'hooks/useEnableHotjarTreatment';

import AuthRoute from './AuthRoute';
import useCachedCMSDataEffect from './useCachedCMSDataEffect';
import useCMSDataEffect from './useCMSDataEffect';

setConfig({
  reloadHooks: false,
});

const AppRouterHelmet = () => {
  const eventName = useSelector(selectLocalizedEventName);
  const locale = useSelector(selectLocale);

  return (
    <CustomHelmet htmlAttributes={{ lang: locale }}>
      <meta charSet="utf-8" />
      <title>{eventName}</title>
      <meta name="description" content={eventName} />
    </CustomHelmet>
  );
};

const triggerHotjarSurvey = (email, role) => {
  if (window.hj) {
    window.hj('identify', email, {
      domain: document.location.host,
      role,
    });
  } else {
    setTimeout(() => {
      triggerHotjarSurvey(email, role);
    }, 1000);
  }
};

const AppRouter = () => {
  const { features, customPage } = useSelector((s) => s.eventConfigs);
  const defaultTheme = useDefaultTheme();
  const { userSession } = useContext(UserSessionContext);
  const locale = useSelector(selectLocale);
  const enableHotjar = useEnableHotjarTreatment();

  useEffect(() => {
    if (enableHotjar) {
      hotjar.initialize(HOTJAR_ID, HOTJAR_SURVEY_VERSION);
      const { customAttendeeFields } = userSession || {};
      let role = 'attendee';
      if (customAttendeeFields?.vexpo_exhibitor_booth_id) {
        role = 'exhibitor';
      } else {
        role = userSession.role;
      }
      triggerHotjarSurvey(userSession.email, role);
    }
  }, [enableHotjar]);

  useCachedCMSDataEffect();
  useCMSDataEffect();

  const flattenedCustomPage = features.includes('multipleCustomPages') ? (customPage || []).flatMap((curr) => curr.pages) : [];
  const isLoggedIn = useMemo(() => Boolean((userSession || {}).authToken), [userSession]);
  const enableMyProfile = useMyProfileTreatment();
  const showProfileModal = useMemo(() => isLoggedIn && enableMyProfile, [isLoggedIn, enableMyProfile]);

  return (
    <ThemeProvider theme={defaultTheme}>
      <AppRouterHelmet />
      {features.includes('networkCheck') && <NetworkCheckModal />}
      {features.includes('singleUserSession') && <ConcurrentSessionTracker />}
      {showProfileModal && <ProfileModal />}
      {showProfileModal && <FirstTimeProfilePictureModal />}
      {!!locale && (
        <ContentPageFrame>
          <Switch>
            <AuthRoute
              exact
              path={[Routes.root, Routes.lobby]}
              route="lobby"
              component={MainLobbyContainer}
            />
            <AuthRoute
              path={Routes.booth()}
              route="booth"
              component={BoothProfileRootContainer}
            />
            <AuthRoute
              exact
              path={Routes.hall}
              route="hall"
              component={HallPage}
            />
            <AuthRoute
              exact
              path={Routes.webinar}
              route="webinar"
              component={WebinarPageContainer}
            />
            <AuthRoute
              exact
              path={Routes.webinarSpeaker()}
              route="webinar"
              component={WebinarSpeakerDetailsContainer}
            />
            <AuthRoute
              exact
              path={Routes.webinarSession()}
              route="webinar"
              component={WebinarDetailContainer}
            />
            <AuthRoute
              exact
              path={Routes.participant}
              route="participant"
              component={features.includes('hktdcFiltering') ? ParticipantPageContainerHKTDC : ParticipantPageContainer}
            />
            <AuthRoute
              exact
              path={Routes.search}
              route="search"
              component={SearchResultPageContainer}
            />
            <AuthRoute
              exact
              path={Routes.products}
              route="product"
              component={ProductPage}
            />
            <AuthRoute
              exact
              path={Routes.productDetail()}
              route="productDetail"
              component={ProductDetailContainer}
            />
            {features.includes('customPage') && (
            <AuthRoute
              exact
              path={Routes.custom}
              route="custom"
              component={CustomPageContainer}
            />
            )}
            <AuthRoute
              exact
              path={Routes.landing}
              route="landing"
              component={LandingPage}
            />
            <AuthRoute
              exact
              path={Routes.contacts}
              route="contacts"
              component={ContactsPageContainer}
            />
            <AuthRoute
              exact
              path={Routes.boothPreview()}
              route="boothPreview"
              component={BoothPreviewPage}
            />
            <AuthRoute
              exact
              path={Routes.speakers}
              route="speakers"
              component={SpeakersPageContainer}
            />
            {flattenedCustomPage.map(({ path }) => Object.values(path || {}).map((localizedPath) => (
              <AuthRoute
                exact
                path={localizedPath}
                route={localizedPath?.substring(1)}
                component={CustomPageContainerV2}
              />
            )))}
            <AuthRoute
              exact
              path={Routes.networkingLounge}
              route="networkingLounge"
              component={NetworkingLounge}
            />
            <Route component={PageNoFound} />
          </Switch>
        </ContentPageFrame>
      )}
    </ThemeProvider>
  );
};

export default hot(AppRouter);
